import type { FlowApprovalSubmission } from '@reflex/flows';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { flowsApi } from './api.js';
import { applyNodeUpdateToCache, applyRunSummaryPatchToCache } from './flow-runs-list-cache.js';
import { invalidationDelayMs } from './invalidation-window.js';
import { flowToCreatePayload } from './duplicate-flow.js';
import { useSocketMessage } from '@reflex/ui/client/socket';
import { useOrgKey } from '@reflex/ui/client/context';
import type { WsServerMessage } from '@reflex/shared';
import type {
  FlowDefinition,
  FlowRunSummary,
  FlowRunNode,
  CreateFlowDefinition,
  UpdateFlowDefinition,
} from '@reflex/flows';

/**
 * Keeps the flow-runs list fresh from `flow_event` WS messages without
 * refetching for the high-frequency events (WebSocket-first, per AGENTS.md).
 *
 * - `flow_run:created` refetches: it is one-shot per run and needs the full
 *   row plus correct list membership (which flow's list it joins).
 * - `flow_run:updated` patches the cached row in place. This event fires
 *   often while runs are active; refetching here produced a synchronized
 *   refetch herd across every mounted list observer and every connected
 *   client (see the `/flow-runs` latency fix). The payload carries only
 *   summary-level fields, so a local merge keeps the list correct. The one
 *   exception is `waiting_approval`: the server filters those runs by assignee
 *   (`filterRunsByAssignee`) and the payload carries no assignee info, so:
 *     - Entering `waiting_approval` refetches, because a non-assignee's row
 *       must be dropped (an in-place merge would leak it as awaiting approval).
 *     - Leaving it refetches only for runs WE dropped on entry (tracked in
 *       `droppedWhileWaiting`): the row is gone from cache, so the patch can't
 *       restore it (it never inserts). Runs we still hold are visible in every
 *       non-waiting state, so those just patch in place.
 * - `flow_run:node_updated` fires once per node per step and only affects
 *   each row's active-node count, applied straight to the cache.
 *
 * Other events leave the rendered rows unchanged.
 */
function useFlowRunsListInvalidation(queryKey: unknown[]) {
  const qc = useQueryClient();
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;
  // Runs this client refetched out of its list on entering `waiting_approval`
  // (the server filters them out for non-assignees). When such a run later
  // leaves `waiting_approval` it becomes visible again, but the row is absent
  // so `applyRunSummaryPatch` can't bring it back; refetch instead.
  const droppedWhileWaiting = useRef<Set<string>>(new Set());

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'flow_event') return;

        if (msg.flowEventType === 'flow_run:created') {
          qc.invalidateQueries({ queryKey: queryKeyRef.current });
          return;
        }

        if (msg.flowEventType === 'flow_run:updated') {
          const payload = msg.payload as { run?: unknown } | undefined;
          const run = payload?.run as (Partial<FlowRunSummary> & { id?: unknown }) | undefined;
          if (typeof run?.id !== 'string') return;
          const runId = run.id;

          if (run.status === 'waiting_approval') {
            // Membership may change (assignee-filtered). Remember the run so we
            // can restore it on exit even if the refetch drops it for us here.
            droppedWhileWaiting.current.add(runId);
            qc.invalidateQueries({ queryKey: queryKeyRef.current });
            return;
          }

          if (droppedWhileWaiting.current.has(runId)) {
            // Leaving waiting_approval: the run is visible again but absent from
            // our cache, so refetch to bring it back.
            droppedWhileWaiting.current.delete(runId);
            qc.invalidateQueries({ queryKey: queryKeyRef.current });
            return;
          }

          const patch = { ...run, id: runId };
          // The `queryKey` prefix matches the global and per-flow lists (paged
          // infinite caches) and the active-runs snapshot (a plain array, which
          // the dispatcher skips by shape). The helper maps over every page.
          qc.setQueriesData({ queryKey: queryKeyRef.current }, (data) =>
            applyRunSummaryPatchToCache(data, patch),
          );
          return;
        }

        if (msg.flowEventType === 'flow_run:node_updated') {
          const payload = msg.payload as { runId?: unknown; node?: unknown } | undefined;
          const runId = typeof payload?.runId === 'string' ? payload.runId : undefined;
          const node = payload?.node as FlowRunNode | undefined;
          if (!runId || !node) return;
          // The `queryKey` prefix matches both the global and per-flow paged
          // lists; the helper maps the patch over every page.
          qc.setQueriesData({ queryKey: queryKeyRef.current }, (data) =>
            applyNodeUpdateToCache(data, runId, node),
          );
        }
      },
      [qc],
    ),
  );
}

/**
 * Schedules one debounced `invalidateQueries` per window, with the window
 * length jittered by {@link invalidationDelayMs}.
 *
 * The debounce alone collapses a burst *within* one client. It does not
 * disperse the herd *across* clients — it aligns it, which is what drove the
 * `GET /api/flow-runs/inbox` latency spikes: ~30 requests inside one second
 * from a single org, each issuing two parallel queries against a shared
 * 30-connection pool, pushing p99 from ~100ms to ~2.5-4.9s.
 */
function useJitteredInvalidation(queryKey: readonly unknown[], windowMs: number): () => void {
  const qc = useQueryClient();
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  return useCallback(() => {
    // One refetch scheduled per window; further events in the window fold in.
    if (timerRef.current !== null) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      qc.invalidateQueries({ queryKey: queryKeyRef.current });
    }, invalidationDelayMs(windowMs));
  }, [qc, windowMs]);
}

// Coalesce a burst of run events into a single stats refetch. `flow_run:updated`
// fires on every run status transition and is exactly the firehose Phase 1
// removed from the list path; invalidating the (org-wide grouped) stats query
// per event would refetch it across every connected client. A jittered
// trailing window collapses a burst into one refetch per client and staggers
// that refetch across clients (see `useJitteredInvalidation`).
const STATS_INVALIDATE_DEBOUNCE_MS = 2_000;

/**
 * Refetch the per-flow stats rollup when runs are created or change status,
 * debounced. Counts move only on `flow_run:created` (a new run) and
 * `flow_run:updated` (a status transition between buckets); the high-frequency
 * `flow_run:node_updated` never changes a count, so it is ignored here. The
 * stats payload is bounded by flow count, so a single coalesced refetch per
 * burst keeps it correct without reintroducing a refetch herd.
 */
function useFlowRunStatsInvalidation(queryKey: readonly unknown[]) {
  const scheduleInvalidate = useJitteredInvalidation(queryKey, STATS_INVALIDATE_DEBOUNCE_MS);
  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'flow_event') return;
        if (msg.flowEventType !== 'flow_run:created' && msg.flowEventType !== 'flow_run:updated') {
          return;
        }
        scheduleInvalidate();
      },
      [scheduleInvalidate],
    ),
  );
}

/**
 * Invalidates the `['flow-run', runId]` and `['flow-run-nodes', runId]` caches
 * whenever a `flow_event` WS message arrives. Used by `useFlowRun` and
 * `useFlowRunNodes` instead of `refetchInterval` polling (polling violates
 * the WebSocket-first rule in AGENTS.md and contributes to event-loop
 * starvation under concurrent load).
 *
 * Filtering is deliberately permissive: if the payload explicitly carries a
 * `runId` / `run.id` that doesn't match, we skip. Otherwise (e.g. node-level
 * events that only carry `nodeId`) we invalidate — false positives are
 * cheap, missed updates are not.
 */
function useFlowRunEventInvalidation(runId: string | undefined) {
  const qc = useQueryClient();
  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (!runId) return;
        if (msg.type !== 'flow_event') return;
        const payload = msg.payload as Record<string, unknown> | undefined;
        const run = payload?.run as { id?: string } | undefined;
        const payloadRunId =
          run?.id ?? (typeof payload?.runId === 'string' ? (payload.runId as string) : undefined);
        if (payloadRunId && payloadRunId !== runId) return;
        qc.invalidateQueries({ queryKey: ['flow-run', runId] });
        qc.invalidateQueries({ queryKey: ['flow-run-nodes', runId] });
      },
      [qc, runId],
    ),
  );
}

// Flow query keys carry the active org segment so cross-org cache hits
// don't briefly render the previous org's flows on switch. The runId-scoped
// keys (`flow-run`, `flow-run-nodes`) keep their flat shape because the
// runId itself is globally unique and the per-run pages mount under
// `key={runId}` so cache reuse across orgs is impossible there.
type OrgKey = ReturnType<typeof useOrgKey>;
const flowsKey = (org: OrgKey) => ['flows', org] as const;
const flowKey = (org: OrgKey, flowId: string | undefined) => ['flow', org, flowId] as const;
const flowRunsKey = (org: OrgKey, flowId?: string) =>
  flowId ? (['flow-runs', org, { flowId }] as const) : (['flow-runs', org] as const);
const flowRunsInboxKey = (org: OrgKey) => ['flow-runs-inbox', org] as const;
const flowRunStatsKey = (org: OrgKey) => ['flow-run-stats', org] as const;
const fieldOptionsKey = (org: OrgKey, fieldType: string | undefined) =>
  ['flow-field-options', org, fieldType] as const;
/** Authoritative query key for the action catalog; use in tests to seed the cache. */
export const actionCatalogKey = (org: OrgKey) => ['flow-action-catalog', org] as const;

export function useFlows() {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: flowsKey(orgKey),
    queryFn: () => flowsApi.listFlows(),
  });
}

/**
 * Every action the current org can run, feeding the editor's action picker
 * and the derived I/O shown for `action` nodes.
 *
 * Org-keyed: the server narrows the catalog to the plugins the caller's org
 * has installed, and the org switcher navigates client-side, so a bare key
 * would serve one org's action list inside another.
 *
 * Cached for the session like the event catalog, but for a weaker reason —
 * this list is a function of the org's *installed plugins*, not of the
 * deploy, so it can genuinely change under us. Installing Slack has to reach
 * the picker, or a user who acts on the "connect an integration" empty state
 * is told to do the thing they just did. That's a push, not a deadline: the
 * subscription below invalidates on the same `org:plugin_installation_changed`
 * broadcast the host's plugin queries listen to, so there's no window in
 * which the picker is knowingly wrong and no refetch when nothing changed.
 */
export function useFlowActionCatalog() {
  const orgKey = useOrgKey();
  const queryClient = useQueryClient();

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'org:plugin_installation_changed') return;
        if (msg.organizationId !== orgKey) return;
        void queryClient.invalidateQueries({ queryKey: actionCatalogKey(orgKey) });
      },
      [orgKey, queryClient],
    ),
  );

  return useQuery({
    queryKey: actionCatalogKey(orgKey),
    queryFn: () => flowsApi.getActionCatalog(),
    staleTime: Infinity,
  });
}

/** Capability reference data registered by the host and installed plugins. */
export function useFlowCapabilities() {
  return useQuery({
    queryKey: ['flow-capabilities'],
    queryFn: () => flowsApi.getCapabilities(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The org-wide event catalog (every `(source, event)` plugins declared,
 * with payload JSON Schemas). Genuinely deploy-fixed reference data — the
 * server returns the whole registry without narrowing it to the org — so
 * unlike the action catalog it never goes stale within a session.
 */
export function useFlowEventCatalog() {
  return useQuery({
    queryKey: ['flow-event-catalog'],
    queryFn: () => flowsApi.getEventCatalog(),
    staleTime: Infinity,
  });
}

/**
 * Options for a semantic payload-field type (e.g. 'linear:user'), feeding the
 * conditions editor's typed pickers. Org-keyed: providers resolve the caller
 * org's integration connection, and the org switcher navigates client-side,
 * so an org-less key would serve one org's options inside another.
 */
export function useFieldOptions(fieldType: string | undefined) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: fieldOptionsKey(orgKey, fieldType),
    queryFn: () => flowsApi.getFieldOptions(fieldType!),
    enabled: !!fieldType,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFlow(flowId: string | undefined) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: flowKey(orgKey, flowId),
    queryFn: () => flowsApi.getFlow(flowId!),
    enabled: !!flowId,
  });
}

export function useCreateFlow() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (data: CreateFlowDefinition) => flowsApi.createFlow(data),
    onSuccess: (flow) => {
      qc.setQueryData<FlowDefinition>(flowKey(orgKey, flow.id), flow);
      qc.invalidateQueries({ queryKey: flowsKey(orgKey) });
    },
  });
}

export function useDuplicateFlow() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (flow: FlowDefinition) => flowsApi.createFlow(flowToCreatePayload(flow)),
    onSuccess: (created) => {
      qc.setQueryData<FlowDefinition>(flowKey(orgKey, created.id), created);
      qc.invalidateQueries({ queryKey: flowsKey(orgKey) });
    },
  });
}

export function useUpdateFlow() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    // The flow editor's autosave committer: fires on every field commit and
    // node drag with optimistic UI and a failure-only toast, so keep it off the
    // host's global top progress bar (`topProgress: false` meta flag).
    meta: { topProgress: false },
    mutationFn: ({
      id,
      data,
      skipConnectivityChecks,
    }: {
      id: string;
      data: UpdateFlowDefinition;
      /**
       * Set true for incremental editor mutations that may produce a
       * mid-edit disconnected graph (e.g. dropping a node before drawing
       * its edge). Publish-time validation in the server route catches
       * structural problems before they ship.
       */
      skipConnectivityChecks?: boolean;
    }) => flowsApi.updateFlow(id, data, { skipConnectivityChecks }),
    onSuccess: (flow) => {
      qc.setQueryData<FlowDefinition>(flowKey(orgKey, flow.id), flow);
      qc.invalidateQueries({ queryKey: flowsKey(orgKey) });
      qc.invalidateQueries({ queryKey: flowDiffKeyPrefix(orgKey, flow.id) });
      // The suggested change title describes a diff that just moved. Mark it
      // stale without refetching: this fires on every autosave, and an
      // eager refetch would spend an LLM call per keystroke-commit.
      qc.invalidateQueries({
        queryKey: flowChangeTitleKey(orgKey, flow.id),
        refetchType: 'none',
      });
    },
  });
}

export function useDeleteFlow() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => flowsApi.deleteFlow(id),
    onSuccess: (_result, id) => {
      qc.removeQueries({ queryKey: flowKey(orgKey, id) });
      qc.invalidateQueries({ queryKey: flowsKey(orgKey) });
    },
  });
}

export function useToggleFlow() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      flowsApi.toggleFlow(id, enabled),
    onSuccess: (flow) => {
      qc.setQueryData<FlowDefinition>(flowKey(orgKey, flow.id), flow);
      qc.invalidateQueries({ queryKey: flowsKey(orgKey) });
    },
  });
}

export function useTriggerFlow() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({
      flowId,
      triggerData,
      triggerNodeId,
    }: {
      flowId: string;
      triggerData?: Record<string, unknown>;
      triggerNodeId?: string;
    }) => flowsApi.triggerFlow(flowId, triggerData, triggerNodeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flow-runs', orgKey] });
      qc.invalidateQueries({ queryKey: flowRunStatsKey(orgKey) });
    },
  });
}

/**
 * Debounced WS-driven invalidation for `['flow-runs-inbox', org]`. The
 * inbox cares about exactly one transition: a run crossing into a
 * terminal status it renders (`error` / `completed`). Skip:
 *   - `flow_run:created` — new runs are `running`, never inbox material.
 *   - `flow_run:node_updated` — node progress doesn't move the run status.
 *   - `flow_run:updated` whose new status is not `error` / `completed`
 *     (e.g. `waiting_approval` — that's a decision-gate concern).
 * A burst of terminal updates collapses into one refetch via a jittered ~2-4s
 * window (see {@link useJitteredInvalidation}), so neither a wave of
 * orchestrator completions nor the org-wide broadcast that announces it
 * triggers a refetch herd.
 */
const INBOX_TERMINAL_STATUSES = new Set(['error', 'completed']);
const INBOX_REFETCH_DEBOUNCE_MS = 2_000;

function useInboxRunsInvalidation(queryKey: unknown[]) {
  const scheduleInvalidate = useJitteredInvalidation(queryKey, INBOX_REFETCH_DEBOUNCE_MS);

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'flow_event') return;
        if (msg.flowEventType !== 'flow_run:updated') return;
        const payload = msg.payload as { run?: { status?: unknown } } | undefined;
        const status = payload?.run?.status;
        if (typeof status !== 'string' || !INBOX_TERMINAL_STATUSES.has(status)) return;
        scheduleInvalidate();
      },
      [scheduleInvalidate],
    ),
  );
}

/**
 * Bounded, owner-scoped flow-runs feed for the inbox. Returns
 * `{ errors, recentCompleted }` for runs the caller owns (attributed to
 * them via `userId` or owned by a flow in their user/team tiers). This is
 * the inbox's only flow-runs dependency — it deliberately does NOT call
 * {@link useFlowRuns} so the inbox never rides the org-wide runs-list
 * invalidation firehose.
 */
export function useInboxFlowRuns() {
  const orgKey = useOrgKey();
  useInboxRunsInvalidation(['flow-runs-inbox', orgKey]);
  return useQuery({
    queryKey: flowRunsInboxKey(orgKey),
    // Forward react-query's abort signal so a refetch superseded by the next
    // invalidation actually cancels the in-flight request server-side instead
    // of running to completion and competing for the connection pool.
    queryFn: ({ signal }) => flowsApi.getInboxRuns({ signal }),
  });
}

/**
 * Cursor-paginated runs list. Returns the loaded rows flattened across pages as
 * `data` (so list consumers read a `FlowRunSummary[]`), alongside the infinite
 * query's `fetchNextPage` / `hasNextPage` / `isFetchingNextPage` for a
 * load-more affordance. The cache stays keyed on `flowId` only (never status),
 * so the WS cache patches in `useFlowRunsListInvalidation` can patch rows in
 * place across pages; status filtering stays client-side.
 *
 * Callers that need the whole history must page through with `fetchNextPage`;
 * there is deliberately no auto-load-everything option, since walking every
 * page on an always-mounted surface (e.g. the inbox) reintroduces the unbounded
 * read this work removes. Dashboard counts come from {@link useFlowRunStats},
 * not from counting this list.
 */
export function useFlowRuns(flowId?: string) {
  const orgKey = useOrgKey();
  useFlowRunsListInvalidation(['flow-runs', orgKey]);

  const query = useInfiniteQuery({
    queryKey: flowRunsKey(orgKey, flowId),
    // Forward react-query's abort signal so a refetch superseded by the next
    // invalidation actually cancels the in-flight request server-side instead
    // of running to completion and competing for the connection pool.
    queryFn: ({ pageParam, signal }) =>
      flowsApi.listFlowRuns(flowId, { cursor: pageParam ?? undefined, signal }),
    initialPageParam: null as string | null,
    // On invalidation react-query re-walks from the head and recomputes each
    // page's cursor from the freshly-fetched previous page (it does NOT reuse
    // stored pageParams), so head insertions keep the pages contiguous.
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const runs = useMemo(
    () => query.data?.pages.flatMap((page) => page?.runs ?? []) ?? [],
    [query.data],
  );
  return { ...query, data: runs };
}

/**
 * Per-flow aggregate rollup (totals, per-status counts, latest run) for the
 * runs dashboards. Computed server-side from cheap grouped queries, so callers
 * get exact counts without loading the full paginated list. Refreshed (not
 * patched) on run create/status-change events via a debounced invalidation
 * (see {@link useFlowRunStatsInvalidation}); the high-frequency
 * `flow_run:node_updated` event does not change counts. `staleTime` keeps
 * incidental remounts from refetching the grouped query needlessly.
 */
export function useFlowRunStats() {
  const orgKey = useOrgKey();
  useFlowRunStatsInvalidation(flowRunStatsKey(orgKey));
  return useQuery({
    queryKey: flowRunStatsKey(orgKey),
    queryFn: ({ signal }) => flowsApi.getFlowRunStats({ signal }),
    staleTime: 10_000,
  });
}

export function useFlowRun(runId: string | undefined) {
  useFlowRunEventInvalidation(runId);

  return useQuery({
    queryKey: ['flow-run', runId],
    queryFn: () => flowsApi.getFlowRun(runId!),
    enabled: !!runId,
  });
}

export function useFlowRunNodes(runId: string | undefined) {
  useFlowRunEventInvalidation(runId);

  return useQuery({
    queryKey: ['flow-run-nodes', runId],
    queryFn: () => flowsApi.getFlowRunNodes(runId!),
    enabled: !!runId,
  });
}

export function useSubmitApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      nodeId,
      response,
    }: {
      runId: string;
      nodeId: string;
      response: FlowApprovalSubmission;
    }) => flowsApi.submitApproval(runId, nodeId, response),
    onSuccess: (_result, { runId }) => {
      qc.invalidateQueries({ queryKey: ['flow-run', runId] });
      qc.invalidateQueries({ queryKey: ['flow-run-nodes', runId] });
    },
  });
}

export function useCancelFlowRun() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (runId: string) => flowsApi.cancelFlowRun(runId),
    onSuccess: (_result, runId) => {
      qc.invalidateQueries({ queryKey: ['flow-run', runId] });
      qc.invalidateQueries({ queryKey: ['flow-runs', orgKey] });
      qc.invalidateQueries({ queryKey: flowRunStatsKey(orgKey) });
    },
  });
}

// ---------------------------------------------------------------------------
// Versioning query keys & hooks
// ---------------------------------------------------------------------------

const flowVersionsKey = (org: OrgKey, flowId: string) => ['flow-versions', org, flowId] as const;
const flowDiffKey = (org: OrgKey, flowId: string, withChanges = false) =>
  ['flow-diff', org, flowId, { changes: withChanges }] as const;
/** Prefix matching both diff variants, for invalidation. */
const flowDiffKeyPrefix = (org: OrgKey, flowId: string) => ['flow-diff', org, flowId] as const;
const flowChangeTitleKey = (org: OrgKey, flowId: string) =>
  ['flow-change-title', org, flowId] as const;

/**
 * Draft-vs-live diff for a flow.
 *
 * `withChanges` opts into the itemized change list. The editor holds this
 * query open for the whole session and re-runs it on every autosave, but only
 * reads the counts — so the list, whose payload scales with the size of the
 * change, is fetched by the publish dialog alone.
 */
export function useFlowDiff(flowId: string | undefined, opts: { withChanges?: boolean } = {}) {
  const orgKey = useOrgKey();
  const withChanges = opts.withChanges ?? false;
  return useQuery({
    queryKey: flowDiffKey(orgKey, flowId!, withChanges),
    queryFn: () => flowsApi.getDiff(flowId!, { withChanges }),
    enabled: !!flowId,
    staleTime: 60_000,
  });
}

/**
 * LLM-suggested title for the change about to be published, prefilled into
 * the publish dialog's note field.
 *
 * A query rather than a mutation so one suggestion is cached per (org, flow)
 * and reopening the dialog doesn't spend another LLM call: `staleTime:
 * Infinity` holds it until an edit invalidates the key (see
 * {@link useUpdateFlow}), at which point the next open re-titles the new
 * diff. `retry: false` keeps a failed suggestion from retrying — the dialog
 * falls back to the deterministic title it can compute from the diff.
 */
export function useFlowChangeTitle(flowId: string | undefined, enabled = true) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: flowChangeTitleKey(orgKey, flowId!),
    queryFn: () => flowsApi.getChangeTitle(flowId!),
    enabled: !!flowId && enabled,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Ask for another change title. A mutation, not a query refetch: Suggest
 * again is a user action (it should show pending state even when the
 * cached title is still "fresh"), and it can tell the model which title
 * to avoid repeating.
 */
export function useRefreshFlowChangeTitle() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ flowId, previousTitle }: { flowId: string; previousTitle?: string }) =>
      flowsApi.getChangeTitle(flowId, { previousTitle }),
    onSuccess: (data, { flowId }) => {
      qc.setQueryData(flowChangeTitleKey(orgKey, flowId), data);
    },
  });
}

export function useFlowVersions(flowId: string | undefined, enabled = true) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: flowVersionsKey(orgKey, flowId!),
    queryFn: () => flowsApi.listVersions(flowId!),
    enabled: !!flowId && enabled,
  });
}

const flowVersionKey = (org: OrgKey, flowId: string, versionId: string) =>
  ['flow-version', org, flowId, versionId] as const;

/**
 * One version's full detail, including the graph snapshot. The run page uses
 * this to render the graph a historical run actually executed. Snapshot data
 * is immutable once written, so the cache never goes stale.
 */
export function useFlowVersion(flowId: string | undefined, versionId: string | null | undefined) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: flowVersionKey(orgKey, flowId!, versionId!),
    queryFn: () => flowsApi.getVersion(flowId!, versionId!),
    enabled: !!flowId && !!versionId,
    staleTime: Infinity,
  });
}

/** Invalidate flow + diff + versions caches after a versioning mutation. */
function useFlowVersioningInvalidation() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useCallback(
    (flowId: string) => {
      qc.invalidateQueries({ queryKey: flowKey(orgKey, flowId) });
      qc.invalidateQueries({ queryKey: flowsKey(orgKey) });
      qc.invalidateQueries({ queryKey: flowDiffKeyPrefix(orgKey, flowId) });
      qc.invalidateQueries({ queryKey: flowVersionsKey(orgKey, flowId) });
      // Publishing/restoring resets what "the pending change" even is. Don't
      // refetch it here: publish resolves while the publish dialog is still
      // mounted, and an eager refetch would rewrite the title and the change
      // list to "nothing changed" under the user mid-publish.
      qc.invalidateQueries({
        queryKey: flowChangeTitleKey(orgKey, flowId),
        refetchType: 'none',
      });
    },
    [qc, orgKey],
  );
}

export function usePublishFlow() {
  const invalidate = useFlowVersioningInvalidation();
  return useMutation({
    mutationFn: ({ flowId, note }: { flowId: string; note?: string }) =>
      flowsApi.publish(flowId, { note }),
    onSuccess: (_result, { flowId }) => invalidate(flowId),
  });
}

export function useUnpublishFlow() {
  const invalidate = useFlowVersioningInvalidation();
  return useMutation({
    mutationFn: (flowId: string) => flowsApi.unpublish(flowId),
    onSuccess: (_result, flowId) => invalidate(flowId),
  });
}

export function useDiscardFlowDraft() {
  const invalidate = useFlowVersioningInvalidation();
  return useMutation({
    mutationFn: (flowId: string) => flowsApi.discardDraft(flowId),
    onSuccess: (_result, flowId) => invalidate(flowId),
  });
}

export function useSaveFlowVersion() {
  const invalidate = useFlowVersioningInvalidation();
  return useMutation({
    mutationFn: ({ flowId, note }: { flowId: string; note?: string }) =>
      flowsApi.saveVersion(flowId, { note }),
    onSuccess: (_result, { flowId }) => invalidate(flowId),
  });
}

export function usePublishFlowVersion() {
  const invalidate = useFlowVersioningInvalidation();
  return useMutation({
    mutationFn: ({ flowId, versionId }: { flowId: string; versionId: string }) =>
      flowsApi.publishVersion(flowId, versionId),
    onSuccess: (_result, { flowId }) => invalidate(flowId),
  });
}

/**
 * Rename a saved version. Titles are written by the change-summary LLM task
 * at publish time, so correcting one after the fact has to be one click from
 * the versions list.
 */
export function useUpdateFlowVersionNote() {
  const invalidate = useFlowVersioningInvalidation();
  return useMutation({
    mutationFn: ({
      flowId,
      versionId,
      note,
    }: {
      flowId: string;
      versionId: string;
      note: string | null;
    }) => flowsApi.updateVersionNote(flowId, versionId, note),
    onSuccess: (_result, { flowId }) => invalidate(flowId),
  });
}

export function useRestoreFlowVersion() {
  const invalidate = useFlowVersioningInvalidation();
  return useMutation({
    mutationFn: ({
      flowId,
      versionId,
      force,
    }: {
      flowId: string;
      versionId: string;
      force?: boolean;
    }) => flowsApi.restoreVersion(flowId, versionId, { force }),
    onSuccess: (_result, { flowId }) => invalidate(flowId),
  });
}

// Editor chat persistence now lives in the shared `useEditChatSession` /
// `useClearEditChatSession` hooks (plugin-chat); the flow editor calls them
// with basePath '/flows'. See FlowEditorPage.
